import React, { Suspense, lazy, startTransition, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import RichText from 'mui/components/elements/RichText';

import ToggleButton from 'mui/components/elements/DS_ToggleButton';
import { StyledContainer, StyledSubHeading, HoverGradient, StyledRichText } from './CustomContentStyles';

const Markdown = lazy(() => import('mui/components/elements/DS_Markdown'));

export default function CustomContent({
  subHeading,
  contentHeading,
  contentRt,
  isChildren = false,
  disableCollapsingContent,
  className,
}) {
  const HOVERED_CONTAINER_HEIGHT = 500;

  const styledContainerRef = useRef(null);
  const [isContentExpanded, setIsContentExpanded] = useState(true);
  const [contentHeight, setContentHeight] = useState(null);
  const [isTogglingContentDisabled, setIsTogglingContentDisabled] = useState(disableCollapsingContent);

  useEffect(() => {
    if (isTogglingContentDisabled || !styledContainerRef?.current) return;

    const height = styledContainerRef.current?.offsetHeight;
    if (!height || height === 0) return;
    startTransition(() => {
      if (height > HOVERED_CONTAINER_HEIGHT) setIsContentExpanded(false);
      else setIsTogglingContentDisabled(true);

      setContentHeight(height);
    });
  }, []);

  return (
    <section className={className}>
      <StyledContainer
        ref={styledContainerRef}
        isChildren={isChildren}
        isExpanded={isContentExpanded}
        hoveredHeight={HOVERED_CONTAINER_HEIGHT}
      >
        {subHeading && (
          <StyledSubHeading variant="DS_labelSmallRegular" component="p">
            {subHeading}
          </StyledSubHeading>
        )}
        {contentHeading?.markdown?.html && (
          <Suspense fallback={<div />}>
            <Markdown>{contentHeading.markdown.html}</Markdown>
          </Suspense>
        )}
        {contentRt && (
          <StyledRichText>
            <RichText document={contentRt} />
          </StyledRichText>
        )}
        {!isContentExpanded && <HoverGradient isChildren={isChildren} />}
      </StyledContainer>
      {!isTogglingContentDisabled && (
        <ToggleButton
          isContentExpandedState={[isContentExpanded, setIsContentExpanded]}
          contentHeight={contentHeight}
          hoveredContainerHeight={HOVERED_CONTAINER_HEIGHT}
        />
      )}
    </section>
  );
}

CustomContent.propTypes = {
  subHeading: PropTypes.string,
  contentHeading: PropTypes.shape({
    markdown: PropTypes.shape({
      html: PropTypes.string.isRequired,
    }).isRequired,
  }),
  contentRt: PropTypes.object,
  textAlignmentDesktop: PropTypes.string,
  textAlignmentMobile: PropTypes.string,
};
